/**
 * Admin Storefront Overrides — pin any brand's best offer manually.
 * Every pin/unpin writes an audit_logs row.
 */
import { useEffect, useState } from "react";
import { Pin, PinOff, Store, RefreshCw, Search, ChevronRight, History, Calendar, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminStorefrontOverrides() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [offers, setOffers] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Pin Scheduler modal state (iter52)
  const [scheduleFor, setScheduleFor] = useState(null); // offer being scheduled
  const [pinsFrom, setPinsFrom] = useState("");
  const [pinsUntil, setPinsUntil] = useState("");
  const [pinning, setPinning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/storefronts?limit=200${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setRows(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openStorefront = async (sf) => {
    setSelected(sf);
    setOffers([]);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const [offRes, histRes] = await Promise.all([
        api.get(`/v1/storefronts/${sf.id}/offers?limit=100`),
        api.get(`/admin/storefronts/${sf.id}/pin-history?limit=50`),
      ]);
      setOffers(offRes.data.items || []);
      setHistory(histRes.data.items || []);
    } catch { toast.error("Could not load storefront data"); }
    finally { setHistoryLoading(false); }
  };

  // Convert an `<input type=datetime-local>` value to a UTC ISO string.
  const toIso = (localVal) => {
    if (!localVal) return null;
    try { return new Date(localVal).toISOString(); } catch { return null; }
  };

  const pinOffer = async (offerId, scheduled = false) => {
    setPinning(true);
    try {
      const body = { offer_id: offerId };
      if (scheduled) {
        const pf = toIso(pinsFrom);
        const pu = toIso(pinsUntil);
        if (pf) body.pins_from = pf;
        if (pu) body.pins_until = pu;
        if (!pf && !pu) { toast.error("Pick at least a start or end datetime"); setPinning(false); return; }
        if (pf && pu && pu <= pf) { toast.error("End must be after start"); setPinning(false); return; }
      }
      await api.post(`/admin/storefronts/${selected.id}/pin-offer`, body);
      toast.success(scheduled ? "Offer scheduled to pin during the window." : "Offer pinned.");
      setScheduleFor(null);
      setPinsFrom(""); setPinsUntil("");
      await load();
      const [offRes, histRes, listRes] = await Promise.all([
        api.get(`/v1/storefronts/${selected.id}/offers?limit=100`),
        api.get(`/admin/storefronts/${selected.id}/pin-history?limit=50`),
        api.get(`/admin/storefronts?limit=200${q ? `&q=${encodeURIComponent(q)}` : ""}`),
      ]);
      setOffers(offRes.data.items || []);
      setHistory(histRes.data.items || []);
      setRows(listRes.data.items || []);
      setSelected(listRes.data.items.find((row) => row.id === selected.id) || selected);
    } catch (e) { toast.error(e.response?.data?.detail || "Pin failed"); }
    finally { setPinning(false); }
  };

  const unpin = async (sf) => {
    try {
      await api.delete(`/admin/storefronts/${sf.id}/pin-offer`);
      toast.success("Pin removed. Ranking engine is back in charge.");
      await load();
      if (selected?.id === sf.id) {
        const [histRes] = await Promise.all([
          api.get(`/admin/storefronts/${sf.id}/pin-history?limit=50`),
        ]);
        setHistory(histRes.data.items || []);
        setSelected({ ...sf, admin_pinned_offer_id: null });
      }
    } catch { toast.error("Unpin failed"); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-overrides-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="pf-chip pf-chip-cyan w-fit"><Pin className="w-3 h-3" /> Storefront overrides</div>
          <h1 className="font-display text-2xl md:text-3xl text-slate-900 mt-2">Featured Offer Overrides</h1>
          <p className="text-sm text-slate-500 mt-1">Hand-pin any brand's best offer — the ranking engine will defer to your choice.</p>
        </div>
        <button onClick={load} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                data-testid="overrides-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </header>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                 placeholder="Search by name or slug…"
                 className="input pl-9 w-full"
                 data-testid="overrides-search" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.2fr] gap-4">
        {/* Storefront list */}
        <div className="pf-card overflow-hidden max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/60 sticky top-0 z-10 text-left text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-2">Brand</th>
                <th className="px-4 py-2">Best (live)</th>
                <th className="px-4 py-2">Pin</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>}
              {!loading && rows.map((sf) => (
                <tr key={sf.id}
                    onClick={() => openStorefront(sf)}
                    className={`border-t border-white/60 cursor-pointer hover:bg-white/70 ${selected?.id === sf.id ? "bg-cyan-50/70" : ""}`}
                    data-testid={`overrides-row-${sf.id}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-900">{sf.name}</div>
                    <div className="text-[10px] text-slate-500">{sf.slug} · {sf.status}</div>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {sf.best_offer_name || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {sf.admin_pinned_offer_id ? (
                      <span className="pf-chip pf-chip-cyan text-[10px]"><Pin className="w-2.5 h-2.5" /> Pinned</span>
                    ) : (
                      <span className="text-slate-400">Algo</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">No storefronts match.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Detail — offers of the selected brand */}
        <div className="pf-card p-5" data-testid="overrides-detail">
          {!selected ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Store className="w-6 h-6 mx-auto text-slate-400 mb-2" />
              Select a brand to see its offers.
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Selected brand</div>
                <div className="font-display text-slate-900 text-xl">{selected.name}</div>
                {selected.admin_pinned_offer_id && (
                  <div className="mt-2 flex items-center justify-between p-3 rounded-lg bg-cyan-50 border border-cyan-200">
                    <div className="text-xs">
                      <div className="font-semibold text-cyan-800">Currently pinned</div>
                      <div className="text-slate-600 mt-0.5">Algo would have picked: {selected.algo_best_offer_name || "—"}</div>
                      {(selected.admin_pinned_from || selected.admin_pinned_until) && (
                        <div className="text-slate-600 mt-1 inline-flex items-center gap-1"
                             data-testid="overrides-schedule-window">
                          <Calendar className="w-3 h-3" />
                          {selected.admin_pinned_from
                            ? new Date(selected.admin_pinned_from).toLocaleString()
                            : "now"}
                          {" → "}
                          {selected.admin_pinned_until
                            ? new Date(selected.admin_pinned_until).toLocaleString()
                            : "no end"}
                        </div>
                      )}
                    </div>
                    <button onClick={() => unpin(selected)}
                            className="pf-btn-glass px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                            data-testid="overrides-unpin">
                      <PinOff className="w-3 h-3" /> Unpin
                    </button>
                  </div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Pick an offer to pin</div>
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {offers.map((o) => (
                    <div key={o.id}
                         className={`flex items-center justify-between p-3 rounded-lg border ${selected.admin_pinned_offer_id === o.id ? "border-cyan-400 bg-cyan-50" : "border-slate-200 hover:bg-slate-50"}`}
                         data-testid={`overrides-offer-${o.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{o.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono">
                          {o.discount_value}{o.discount_type === "percent" ? "%" : ""} {o.discount_type} · {o.status}
                        </div>
                      </div>
                      {selected.admin_pinned_offer_id === o.id ? (
                        <span className="pf-chip pf-chip-cyan text-[10px]"><Pin className="w-2.5 h-2.5" /> Pinned</span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button onClick={() => pinOffer(o.id)}
                                  disabled={pinning}
                                  className="pf-btn-primary px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                                  data-testid={`overrides-pin-${o.id}`}>
                            <Pin className="w-3 h-3" /> Pin
                          </button>
                          <button onClick={() => { setScheduleFor(o); setPinsFrom(""); setPinsUntil(""); }}
                                  disabled={pinning}
                                  className="pf-btn-glass px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                                  title="Schedule pin"
                                  data-testid={`overrides-schedule-${o.id}`}>
                            <Calendar className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {offers.length === 0 && (
                    <div className="text-xs text-slate-500 p-3">No active offers to pin.</div>
                  )}
                </div>
              </div>

              {/* Pin history timeline */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <History className="w-3.5 h-3.5 text-slate-500" />
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Pin history</div>
                </div>
                {historyLoading ? (
                  <div className="text-xs text-slate-500 p-2">Loading history…</div>
                ) : history.length === 0 ? (
                  <div className="text-xs text-slate-500 p-2" data-testid="overrides-history-empty">
                    No pin actions recorded yet.
                  </div>
                ) : (
                  <ol className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="overrides-history">
                    {history.map((row, i) => {
                      const isPin = row.action === "storefront.pin_offer";
                      const meta = row.metadata || {};
                      return (
                        <li key={i}
                             className={`flex items-start gap-2 p-2 rounded border ${isPin ? "border-cyan-200 bg-cyan-50/40" : "border-slate-200 bg-slate-50"}`}
                             data-testid={`overrides-history-row-${i}`}>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0 ${isPin ? "bg-cyan-500" : "bg-slate-500"}`}>
                            {isPin ? <Pin className="w-3 h-3" /> : <PinOff className="w-3 h-3" />}
                          </div>
                          <div className="flex-1 min-w-0 text-xs">
                            <div className="text-slate-900 font-medium">
                              {isPin
                                ? <>Pinned <span className="font-semibold">{meta.offer_name || meta.offer_id?.slice(0, 8) || "an offer"}</span></>
                                : <>Removed pin{meta.previous_pin_name ? <> from <span className="font-semibold">{meta.previous_pin_name}</span></> : ""}</>}
                            </div>
                            <div className="text-slate-500 mt-0.5">
                              by <span className="font-mono">{row.actor_email || row.actor_id?.slice(0, 8) || "admin"}</span>
                              {" · "}
                              {new Date(row.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pin Scheduler modal */}
      {scheduleFor && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setScheduleFor(null)} data-testid="overrides-schedule-modal">
          <div onClick={(e) => e.stopPropagation()}
               className="pf-card p-6 max-w-md w-full space-y-4 relative">
            <button onClick={() => setScheduleFor(null)}
                    className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
                    aria-label="Close"
                    data-testid="overrides-schedule-close">
              <X className="w-4 h-4" />
            </button>
            <div>
              <div className="pf-chip pf-chip-cyan w-fit"><Calendar className="w-3 h-3" /> Pin scheduler</div>
              <div className="font-display text-slate-900 text-xl mt-2">Schedule this offer's pin</div>
              <div className="text-xs text-slate-500 mt-1">
                {selected?.name} · {scheduleFor.name}
              </div>
            </div>
            <div className="space-y-3">
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Start at</div>
                <input type="datetime-local"
                       value={pinsFrom}
                       onChange={(e) => setPinsFrom(e.target.value)}
                       className="input w-full"
                       data-testid="overrides-schedule-from" />
                <div className="text-[10px] text-slate-500 mt-1">Leave blank to activate immediately.</div>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Ends at</div>
                <input type="datetime-local"
                       value={pinsUntil}
                       onChange={(e) => setPinsUntil(e.target.value)}
                       className="input w-full"
                       data-testid="overrides-schedule-until" />
                <div className="text-[10px] text-slate-500 mt-1">Once past this time, the algorithm reclaims the best-offer slot automatically.</div>
              </label>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setScheduleFor(null)}
                      className="pf-btn-glass px-4 py-2 rounded-full text-xs font-semibold"
                      data-testid="overrides-schedule-cancel">Cancel</button>
              <button onClick={() => pinOffer(scheduleFor.id, true)}
                      disabled={pinning}
                      className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
                      data-testid="overrides-schedule-confirm">
                <Calendar className="w-3 h-3" /> Schedule pin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
